import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styles from './SignupScreen.module.css';
import { apiService } from '../../../utils/api';
import logoSecondLane from '../../../assets/SL-Top-Icon.png';

// SignupScreen
const SignupScreen: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Form validation
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setLoading(true);
      // Call API for registration
      const response = await apiService.signup({
        email,
        password
      });
      
      // Store token in localStorage (user is now authenticated)
      localStorage.setItem('authToken', response.token);
      
      // Force a storage event to trigger auth state update
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'authToken',
        newValue: response.token,
        storageArea: localStorage
      }));
      
      // Check attestation status and redirect accordingly
      const attestationStatus = await apiService.getAttestationStatus();
      if (!attestationStatus.is_attested) {
        navigate('/welcome');
      } else {
        navigate('/deals');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during registration';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerLogoContainer}>
        <img src={logoSecondLane} alt="SecondLane" className={styles.headerLogo} />
      </div>
      <div className={styles.formContainer}>
        <h1 className={styles.title}>Sign Up</h1>
        <p className={styles.description}>
          Create an account to access the SecondLane platform
        </p>
        {error && (
          <div className={styles.error}>
            <span className={styles.errorIcon}>!</span>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email"
              disabled={loading}
              className={styles.input}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              disabled={loading}
              className={styles.input}
              required
            />
          </div>
          <div className={styles.navigationButtons}>
            <button 
              type="submit" 
              className={`${styles.submitButton} ${!loading ? styles.activeButton : ''}`}
              disabled={loading}
            >
              {loading ? 'Signing up...' : 'Sign Up'}
            </button>
          </div>
        </form>
        <div className={styles.links}>
          <p>
            Already have an account? <Link to="/login" className={styles.link}>Log In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupScreen; 